import type { SiteConfig } from "../../api/types";
import { charitySentence, pledgeText } from "../../lib/charity";
import { PAIR_FALLBACK, PROMOTION_FALLBACK } from "../../lib/facts";
import {
  creatorShare,
  platformFee,
  pledgeCeiling,
  pledgeExample,
  tradersPay,
} from "../../lib/fees";
import { Link } from "../../router/router";
import type { FaqItem } from "./faqTypes";

/** Questions about the token, the money and the people behind it. */
export function faqToken(config: SiteConfig | null): FaqItem[] {
  const pair = config?.pair.symbol ?? PAIR_FALLBACK;

  return [
    {
      id: "investment",
      question: "Is $TISM an investment?",
      answer: (
        <div className="stack stack-3">
          <p className="body">
            No. $TISM is a memecoin. It has no intrinsic value, no product, and no revenue. Holding
            it gives you no rights, no share of any revenue, no share of the trading fee, and no
            vote on anything. There is no roadmap, so there is nothing being built that you are
            buying ahead of.
          </p>
          <p className="body">
            The price can go to zero, and that is a normal outcome for a memecoin rather than a
            worst case. Nothing on this site is investment advice, and $TISM is not offered to
            anyone in a place where that would be unlawful.
          </p>
          <p className="body">
            The full version is on the <Link to="/terms">terms page</Link>.
          </p>
        </div>
      ),
    },
    {
      id: "why-eth",
      question: "Why ETH and not a stock token?",
      answer: (
        <p className="body">
          {pair} is what most wallets on the chain already hold, and unlike a tokenized stock it
          carries no issuer-imposed transfer or geographic gating. That says nothing about the rules
          where you are: knowing those is on you.
        </p>
      ),
    },
    {
      id: "charity-amount",
      question: "How much actually goes to charity?",
      answer: (
        <div className="stack stack-3">
          <p className="body">
            {pledgeText(config)}. Every trade pays a platform fee of {platformFee(config)} plus a
            creator tax the creator set once at launch, which goes to the creator wallet. The total
            traders pay is {tradersPay(config)}, and the creator's share is {creatorShare(config)}.
            Half of that share is donated, so the pledge is {pledgeCeiling(config)}.
          </p>
          <p className="body">
            In money, {pledgeExample(config)}. That is the real magnitude, and it is worth knowing
            before you buy anything for charitable reasons. If nothing trades, nothing is donated.
          </p>
          <p className="body">
            Fees are collected in ETH, sold, and donated in Canadian dollars every quarter, with no
            minimum and no maximum. Each closed quarter is listed on the{" "}
            <Link to="/#transparency">transparency table</Link> with the transactions behind it.
          </p>
        </div>
      ),
    },
    {
      id: "charity-name",
      question: "Why is the charity not named yet?",
      answer: (
        <div className="stack stack-3">
          <p className="body">
            Because it has not agreed in writing. Naming a charity next to a memecoin without asking
            first drags it into something it did not sign up for, so the name stays off this site
            until the agreement is in hand.
          </p>
          <p className="body">{charitySentence(config)}</p>
          <p className="body">
            It is a Vancouver charity that works with Autistic people. When the agreement arrives,
            the name appears here on its own, because the server publishes it and this page reads it
            from there.
          </p>
        </div>
      ),
    },
    {
      id: "tax-receipt",
      question: "Can I get a tax receipt?",
      answer: (
        <div className="stack stack-3">
          <p className="body">
            No. Buying $TISM is not a donation. You are buying a token from a bonding curve, and no
            part of what you pay is a gift to a charity.
          </p>
          <p className="body">
            The donation is made later by the team, out of the creator's share of trading fees, so
            any receipt or acknowledgement names the team. No donation receipts are available to
            buyers or claimants.
          </p>
        </div>
      ),
    },
    {
      id: "who",
      question: "Who is behind this?",
      answer: (
        <div className="stack stack-3">
          <p className="body">
            The people behind indexsy.com. The same team runs the $INDEXSY token that the airdrop
            snapshot is taken from.
          </p>
          <p className="body">
            The team made a developer buy at launch. What happened to it, how the airdrop pool is
            funded and the creator wallet are published on the{" "}
            <Link to="/#transparency">home page</Link> from the server.
          </p>
        </div>
      ),
    },
    {
      id: "promotion",
      question: "Do you pay people to promote it?",
      answer: (
        <div className="stack stack-3">
          <p className="body">{config?.team.promotion ?? PROMOTION_FALLBACK}</p>
          <p className="body">
            There is no affiliate scheme, no referral link, and no allocation set aside for
            influencers. Anyone claiming to be a paid promoter or an official partner of $TISM is not
            telling the truth. No charity speaks for $TISM, and $TISM does not speak for any charity.
          </p>
        </div>
      ),
    },
  ];
}
